import { RefObject, useEffect, useLayoutEffect, useState } from 'react';

/**
 * `useLayoutEffect` runs before paint, which is what we want for overflow
 * measurement (avoids a flash of a wrongly-shown control). React logs a warning
 * when `useLayoutEffect` is used during SSR, so fall back to `useEffect` when
 * there is no DOM.
 */
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Custom hook that reports whether the element referenced by `ref` is
 * overflowing its own box (i.e. its content is clipped, `offsetHeight <
 * scrollHeight`).
 *
 * The measurement is driven by a `ResizeObserver` attached before paint, so the
 * result reflects real layout as soon as the element gets dimensions and stays
 * correct across font loads and container resizes. It is SSR-safe and a no-op
 * in environments without `ResizeObserver`, always returning `false` until a
 * real measurement is available.
 *
 * @param ref - Ref to the element whose overflow should be observed.
 * @param enabled - When `false`, observation is skipped and `false` is returned.
 * @returns `true` when the referenced element is overflowing, otherwise `false`.
 *
 * @example
 * ```tsx
 * const contentRef = useRef<HTMLDivElement>(null);
 * const isOverflowing = useOverflowDetection(contentRef, truncated);
 *
 * return (
 *   <div ref={contentRef} className={truncated ? 'm-ellipsis-3lines' : undefined}>
 *     {isOverflowing && <button>See More</button>}
 *   </div>
 * );
 * ```
 */
export function useOverflowDetection(ref: RefObject<HTMLElement>, enabled: boolean): boolean {
  const [isOverflowing, setIsOverflowing] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!enabled || !node || typeof ResizeObserver === 'undefined') {
      setIsOverflowing(false);
      return;
    }

    const measure = () => setIsOverflowing(node.offsetHeight < node.scrollHeight);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);

    return () => observer.disconnect();
  }, [ref, enabled]);

  return isOverflowing;
}
